import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Gift, Copy, Check, Loader2, Share2, Zap } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * Merchant referral widget. Shows the merchant's referral code, free-week
 * balance, who they've referred, and a copy-link button. Drops onto the
 * MerchantDashboard between the loyalty section and the KYC card.
 */
export default function ReferralCard({ onPayWithCredit }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  function load() {
    api.get("/merchant/referrals")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }

  useEffect(() => { load(); }, []);

  async function redeemFreeWeek() {
    setRedeeming(true);
    try {
      const { data: r } = await api.post("/merchant/pay-premium");
      if (r.free_week_applied) {
        toast.success(`Free week applied · ${r.remaining_free_weeks} remaining`);
      } else {
        toast.message("No free week consumed", { description: "Credit balance is 0." });
      }
      load();
      if (onPayWithCredit) onPayWithCredit(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRedeeming(false);
    }
  }

  if (!data) {
    return (
      <div className="bg-white border border-[#114232]/10 rounded-lg p-5 flex items-center gap-2 text-sm text-[#5c6360]" data-testid="referral-card-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your referral code…
      </div>
    );
  }

  const code = data.referral_code;
  const link = `${window.location.origin}/signup?ref=${encodeURIComponent(code)}`;

  function copy() {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2500);
    });
  }

  function share() {
    if (navigator.share) {
      navigator.share({
        title: "Join Nyoksure",
        text: "Cyber-insurance for your business — join Nyoksure and we both get a free week.",
        url: link,
      }).catch(() => {});
    } else {
      copy();
    }
  }

  return (
    <div className="bg-white border border-[#d49a36]/30 rounded-lg p-5" data-testid="referral-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#d49a36]/15 flex items-center justify-center">
            <Gift className="w-5 h-5 text-[#8a5a16]" strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#c15337]">Refer a merchant</div>
            <div className="font-heading text-lg font-bold text-[#114232]">
              Get 1 free week of cover for every paying referral
            </div>
            <div className="text-xs text-[#5c6360] mt-0.5">
              Share your code — once they settle their first premium, you both skip a week.
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">Free weeks</div>
          <div className="font-mono text-2xl font-bold text-[#114232]" data-testid="referral-free-weeks">
            {data.free_weeks_credit}
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <div className="md:col-span-2 space-y-1.5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">Your code</div>
          <div className="flex items-center gap-2">
            <div
              className="font-mono text-2xl font-bold text-[#114232] bg-[#114232]/5 px-3 py-2 rounded-md tracking-widest"
              data-testid="referral-code"
            >
              {code}
            </div>
            <button
              onClick={copy}
              data-testid="referral-copy-link"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-[#114232]/15 rounded-md bg-white hover:bg-[#114232]/5 text-[#114232] transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy link"}
            </button>
            <button
              onClick={share}
              data-testid="referral-share"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-[#114232]/15 rounded-md bg-white hover:bg-[#114232]/5 text-[#114232] transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" />
              Share
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#114232]/5 rounded-md p-3" data-testid="referral-referred-count">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">Referred</div>
            <div className="font-mono text-xl font-bold text-[#114232]">{data.referred_count}</div>
          </div>
          <div className="bg-[#114232]/5 rounded-md p-3" data-testid="referral-rewarded-count">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">Rewarded</div>
            <div className="font-mono text-xl font-bold text-[#114232]">{data.rewarded_count}</div>
          </div>
        </div>
      </div>

      {data.free_weeks_credit > 0 && (
        <div
          className="mt-5 flex items-center justify-between gap-4 p-4 rounded-md bg-gradient-to-r from-[#d49a36]/15 via-[#d49a36]/8 to-transparent border border-[#d49a36]/35 flex-wrap"
          data-testid="referral-credit-cta"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#d49a36]/25 flex items-center justify-center">
              <Zap className="w-5 h-5 text-[#8a5a16]" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-heading text-base font-bold text-[#114232]">
                Skip this week's premium — it's on us
              </div>
              <div className="text-xs text-[#5c6360] mt-0.5">
                You have <span className="font-mono font-semibold text-[#8a5a16]">{data.free_weeks_credit}</span>{" "}
                free week{data.free_weeks_credit === 1 ? "" : "s"} of cover ready to redeem.
              </div>
            </div>
          </div>
          <Button
            onClick={redeemFreeWeek}
            disabled={redeeming}
            data-testid="referral-redeem-btn"
            className="btn-baobab rounded-full px-5 h-10 text-xs uppercase tracking-wider font-semibold hover:bg-[var(--baobab-hover)]"
          >
            {redeeming ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
            Redeem free week
          </Button>
        </div>
      )}

      {data.referred_merchants?.length > 0 && (
        <div className="mt-5 border-t border-[#114232]/10 pt-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold mb-2">Recent referrals</div>
          <ul className="space-y-1.5 text-sm">
            {data.referred_merchants.slice(0, 5).map((r) => (
              <li
                key={r.id}
                data-testid={`referral-referred-${r.id}`}
                className="flex items-center justify-between gap-2"
              >
                <span className="text-[#1b1d1c]">{r.business_name}</span>
                <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${
                  r.rewarded ? "bg-[#114232]/8 text-[#114232]" : "bg-[#d49a36]/12 text-[#8a5a16]"
                }`}>
                  {r.rewarded ? "Rewarded" : "Pending first premium"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
